import { UserTypes } from "@gethere/common/enums";
import { TUser } from "@gethere/common/yup/User";
import { produce } from "immer";
import client, { postChannelMessage } from "../../utils/client";
import mergeEntities from "../../utils/mergeEntities";
import type { RootState } from "../store";
import { USER_ACTIONS } from "../actions";

const initialState = {
  result: null,
  entities: {
    addresses: {},
    paymentMethods: {},
  },
};

const userReducer = (state = initialState, { type, payload }: any) => {
  switch (type) {
    case USER_ACTIONS.ON_LOGIN: {
      return {
        ...state,
        ...payload,
      };
    }

    case USER_ACTIONS.ON_UPDATE: {
      return produce(state, (draft) => {
        if (payload.result) {
          draft.result = payload.result;
        }

        if (payload.entities) {
          draft.entities = mergeEntities(state.entities, payload.entities);
        }
      });
    }

    case USER_ACTIONS.ON_LOGOUT: {
      return initialState;
    }

    case USER_ACTIONS.UPDATE_ADDRESS: {
      return produce(state, (draft) => {
        if (!draft.entities.addresses) draft.entities.addresses = {};
        draft.entities.addresses[payload.id] = payload;
      });
    }

    default:
      return state;
  }
};

// actions

export const onLogin =
  ({ access, accessExpAt, refresh, result, entities }) =>
  (dispatch) => {
    // set access token;
    client.authenticator.setSecureTokens({ refresh, access, accessExpAt });

    postChannelMessage({
      message: "token_refreshed",
      access,
      accessExpAt,
      refresh,
      result,
      entities,
    });

    return dispatch({
      type: USER_ACTIONS.ON_LOGIN,
      payload: { result, entities },
    });
  };

export const onUserUpdate = ({ result, entities }) => ({
  type: USER_ACTIONS.ON_UPDATE,
  payload: { result, entities },
});

export const onUserAddressUpdate = (address) => ({
  type: USER_ACTIONS.UPDATE_ADDRESS,
  payload: address,
});

export const onLogout = () => ({
  type: USER_ACTIONS.ON_LOGOUT,
});
// selectors
export const isUserSelector = (state: RootState) => !!state.user.result;

export const userSelector = (state: RootState): TUser =>
  state.user.entities.users?.[state.user.result];

export const isAdminUserSelector = (user) => user?.type === UserTypes.ADMIN;

export const isAdminSelector = (state: RootState) =>
  isAdminUserSelector(userSelector(state));

export const userNameSelector = (state: RootState) =>
  state.user.entities.users?.[state.user.result]?.name;

export const isUserHasDetails = (state: RootState) => {
  const u = state.user.entities.users?.[state.user.result];
  return u?.name?.first && u?.name?.last;
};

export const userShortName = (name) => {
  return name?.first
    ? name?.first + " " + name?.last?.substring(0, 1) + "."
    : "";
};

export const userInitials = (name) => {
  return name?.first
    ? name?.first?.substring(0, 1) + name?.last?.substring(0, 1)
    : "";
};

export default userReducer;
